import React, { useState, useEffect, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Spinner,
  Alert,
} from "reactstrap"
import Select from "react-select"
import api from "../../services/api"
import OrdemServicoService from "../../services/OrdemServicoService"
import Swal from "sweetalert2"
import AuthService from "../../services/Auth.service"
import { getStatusBadge, formatarData, selectMenuStyles } from "./osHelpers"
import {
  getNomeTipo as getNomeTipoLogic,
  getNomeCondominio as getNomeCondominioLogic,
  isConcluida,
  mapearStatusLabel,
  podeEditar,
} from "./osLogic"
import StatusBadge from "./components/StatusBadge"
import BotaoAcao from "./components/BotaoAcao"
import ModalFormOS from "./components/ModalFormOS"
import { gerarPdfOS } from "./osPdf"

const DetalhesOS = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const user = AuthService.getCurrentUser()
  const fileInputRef = useRef(null)

  // Estados principais
  const [os, setOs] = useState(null)
  const [anexos, setAnexos] = useState([])
  const [tiposServico, setTiposServico] = useState([])
  const [condominios, setCondominios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Estados de edição
  const [editMode, setEditMode] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [editData, setEditData] = useState({
    TIPO_ORDEM: "",
    DS_ORDEM: "",
    DS_ITEM: "",
    RESPONSAVEL: "",
    STATUS: "",
    PROTOCOLO: "",
  })

  // Estados do modal de anexo
  const [modalAnexo, setModalAnexo] = useState(false)
  const [anexoSelecionado, setAnexoSelecionado] = useState(null)
  const [novoAnexo, setNovoAnexo] = useState({
    file: null,
    DS_ANEXO: "",
    RESPONSAVEL: "",
    NOME_RECEBIMENTO: "",
    EMAIL_RECEBIMENTO: "",
    DS_ITEM: "",
    QTD_ITEM: "",
  })
  const [salvandoAnexo, setSalvandoAnexo] = useState(false)

  useEffect(() => {
    carregarDados()
  }, [id])

  const carregarDados = async () => {
    setLoading(true)
    setError(null)

    try {
      // Carregar dados em paralelo
      const [osRes, tiposRes, condominiosRes] = await Promise.all([
        OrdemServicoService.buscarOSPorId(id),
        OrdemServicoService.listarTipos(),
        api.get("/empresa/").catch(() => ({ data: [] })),
      ])

      setOs(osRes)
      setTiposServico(tiposRes)
      setCondominios(condominiosRes.data || condominiosRes)

      // Carregar anexos da OS
      try {
        const anexosRes = await OrdemServicoService.buscarAnexosPorOS(id)
        setAnexos(anexosRes)
      } catch (err) {
        console.log("Sem anexos ou erro ao carregar anexos:", err)
        setAnexos([])
      }

      // Preencher dados de edição
      setEditData({
        TIPO_ORDEM: osRes.TIPO_ORDEM || "",
        DS_ORDEM: osRes.DS_ORDEM || "",
        DS_ITEM: osRes.DS_ITEM || "",
        RESPONSAVEL: osRes.RESPONSAVEL || "",
        STATUS: osRes.STATUS || "ABERTO", // Status do campo STATUS
        PROTOCOLO: osRes.PROTOCOLO || "", // Protocolo do campo PROTOCOLO
      })
    } catch (err) {
      console.error("Erro ao carregar dados:", err)
      setError("Não foi possível carregar os dados da Ordem de Serviço.")
    } finally {
      setLoading(false)
    }
  }

  const getNomeTipo = tipoId => getNomeTipoLogic(tiposServico, tipoId)

  const getNomeCondominio = empresaId =>
    getNomeCondominioLogic(condominios, empresaId)

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }))
  }

  const salvarEdicao = async () => {
    setSalvando(true)
    try {
      const dadosParaSalvar = {
        TIPO_ORDEM: editData.TIPO_ORDEM
          ? parseInt(editData.TIPO_ORDEM)
          : os.TIPO_ORDEM,
        DS_ORDEM: editData.DS_ORDEM,
        DS_ITEM: editData.DS_ITEM,
        RESPONSAVEL: editData.RESPONSAVEL,
        STATUS: editData.STATUS, // Status no campo STATUS
        PROTOCOLO: editData.PROTOCOLO, // Protocolo no campo PROTOCOLO
      }

      await OrdemServicoService.atualizarOS(id, dadosParaSalvar)

      Swal.fire({
        icon: "success",
        title: "OS atualizada!",
        text: "A Ordem de Serviço foi atualizada com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      })

      setEditMode(false)
      carregarDados()
    } catch (err) {
      console.error("Erro ao salvar:", err)
      Swal.fire({
        icon: "error",
        title: "Erro ao salvar",
        text:
          err.response?.data?.message ||
          "Não foi possível atualizar a Ordem de Serviço.",
      })
    } finally {
      setSalvando(false)
    }
  }

  const excluirOS = () => {
    Swal.fire({
      title: "Tem certeza?",
      text: "Esta ação não pode ser desfeita!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
    }).then(async result => {
      if (result.isConfirmed) {
        try {
          // A tabela OS_ANEXO referencia OID_OS e o backend não faz cascade,
          // então excluir a OS direto gera "Foreign key violation". Removemos
          // os anexos primeiro (pode não haver) e só então excluímos a OS.
          let anexosDaOS = []
          try {
            const resultado = await OrdemServicoService.buscarAnexosPorOS(id)
            anexosDaOS = Array.isArray(resultado) ? resultado : []
          } catch (e) {
            anexosDaOS = []
          }
          await Promise.all(
            anexosDaOS.map(a =>
              OrdemServicoService.excluirAnexo(a.OID_ANEXO),
            ),
          )
          await OrdemServicoService.excluirOS(id)
          Swal.fire("Excluída!", "A Ordem de Serviço foi excluída.", "success")
          navigate("/ordem-servico")
        } catch (err) {
          console.error("Erro ao excluir:", err)
          Swal.fire("Erro!", "Não foi possível excluir a OS.", "error")
        }
      }
    })
  }

  // ========== ANEXOS ==========

  const abrirModalNovoAnexo = () => {
    setAnexoSelecionado(null)
    setNovoAnexo({
      file: null,
      DS_ANEXO: "",
      RESPONSAVEL: user?.user?.DS_NOME || "",
      NOME_RECEBIMENTO: "",
      EMAIL_RECEBIMENTO: "",
      DS_ITEM: "",
      QTD_ITEM: "",
    })
    setModalAnexo(true)
  }

  const abrirModalEditarAnexo = anexo => {
    setAnexoSelecionado(anexo)
    setNovoAnexo({
      file: null,
      DS_ANEXO: anexo.DS_ANEXO || "",
      RESPONSAVEL: anexo.RESPONSAVEL || "",
      NOME_RECEBIMENTO: anexo.NOME_RECEBIMENTO || "",
      EMAIL_RECEBIMENTO: anexo.EMAIL_RECEBIMENTO || "",
      DS_ITEM: anexo.DS_ITEM || "",
      QTD_ITEM: anexo.QTD_ITEM || "",
    })
    setModalAnexo(true)
  }

  const handleAnexoChange = (field, value) => {
    setNovoAnexo(prev => ({ ...prev, [field]: value }))
  }

  const handleFileChange = e => {
    const file = e.target.files[0]
    if (file) {
      setNovoAnexo(prev => ({ ...prev, file }))
    }
  }

  const salvarAnexo = async () => {
    if (!novoAnexo.file && !anexoSelecionado) {
      Swal.fire({
        icon: "warning",
        text: "Selecione um arquivo para anexar",
      })
      return
    }

    setSalvandoAnexo(true)

    try {
      const dados = {
        ...novoAnexo,
        OID_OS: id,
      }

      // API exige o campo file no PUT; em edição de metadados (item) sem novo
      // arquivo, enviamos um placeholder vazio (mesmo padrão do CriarOS).
      if (anexoSelecionado && !dados.file) {
        const emptyBlob = new Blob([""], { type: "text/plain" })
        dados.file = new File(
          [emptyBlob],
          `item_${anexoSelecionado.OID_ANEXO || "edit"}.txt`,
          { type: "text/plain" },
        )
      }

      if (anexoSelecionado) {
        await OrdemServicoService.atualizarAnexo(
          anexoSelecionado.OID_ANEXO,
          dados,
        )
        Swal.fire({
          icon: "success",
          title: "Anexo atualizado!",
          timer: 2000,
          showConfirmButton: false,
        })
      } else {
        await OrdemServicoService.criarAnexo(dados)
        Swal.fire({
          icon: "success",
          title: "Anexo adicionado!",
          timer: 2000,
          showConfirmButton: false,
        })
      }

      setModalAnexo(false)
      carregarDados()
    } catch (err) {
      console.error("Erro ao salvar anexo:", err)
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível salvar o anexo.",
      })
    } finally {
      setSalvandoAnexo(false)
    }
  }

  const excluirAnexo = anexo => {
    Swal.fire({
      title: "Excluir anexo?",
      text: `Deseja excluir o anexo "${anexo.DS_ANEXO || "sem descrição"}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Excluir",
      cancelButtonText: "Cancelar",
    }).then(async result => {
      if (result.isConfirmed) {
        try {
          await OrdemServicoService.excluirAnexo(anexo.OID_ANEXO)
          Swal.fire("Excluído!", "O anexo foi excluído.", "success")
          carregarDados()
        } catch (err) {
          console.error("Erro ao excluir anexo:", err)
          Swal.fire("Erro!", "Não foi possível excluir o anexo.", "error")
        }
      }
    })
  }

  const enviarEmailAnexo = async anexo => {
    try {
      await OrdemServicoService.dispararEmailAnexo(anexo.OID_ANEXO)
      Swal.fire({
        icon: "success",
        title: "Email enviado!",
        text: `Email enviado para ${anexo.EMAIL_RECEBIMENTO || "o destinatário"}`,
        timer: 3000,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error("Erro ao enviar email:", err)
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível enviar o email.",
      })
    }
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Carregando dados...</p>
          </div>
        </Container>
      </div>
    )
  }

  if (error || !os) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            {error || "Ordem de Serviço não encontrada."}
          </Alert>
          <Button color="primary" onClick={() => navigate("/ordem-servico")}>
            <i className="bx bx-arrow-back me-1"></i>
            Voltar
          </Button>
        </Container>
      </div>
    )
  }

  const statusInfo = getStatusBadge(os.STATUS)
  const concluida = isConcluida(os.STATUS)
  // Só permite editar OS em status editável (Em aberto / Em andamento).
  // OS concluída/cancelada — ou com status inválido/corrompido (ex.: número de
  // protocolo gravado no campo STATUS) — fica com o botão Editar desabilitado,
  // mesma regra usada na ListaOS.
  const editavel = podeEditar(mapearStatusLabel(os.STATUS))

  const baixarPdf = () => {
    const itensPdf = anexos
      .filter(a => a.DS_ITEM)
      .map(a => ({
        nome: a.DS_ITEM,
        quantidade: a.QTD_ITEM ?? "—",
      }))

    const assinaturaAnexo = anexos.find(
      a =>
        (a.DS_ANEXO || "").toLowerCase().includes("assinatura") ||
        a.IMAGEM_BASE64_STRING,
    )
    let assinaturaData = null
    if (assinaturaAnexo?.IMAGEM_BASE64_STRING) {
      assinaturaData = assinaturaAnexo.IMAGEM_BASE64_STRING
    }

    gerarPdfOS({
      protocolo:
        os.PROTOCOLO || `OS-${String(os.OID_OS).padStart(4, "0")}`,
      data: formatarData(os.DT_CRIACAO || os.DT_ULT_ALTER),
      condominio: getNomeCondominio(os.OID_EMPRESA),
      responsavel: os.RESPONSAVEL || "—",
      tipo: getNomeTipo(os.TIPO_ORDEM),
      status: mapearStatusLabel(os.STATUS),
      descricao: os.DS_ORDEM || "",
      itens: itensPdf.length
        ? itensPdf
        : os.DS_ITEM
          ? [{ nome: os.DS_ITEM, quantidade: "—" }]
          : [],
      assinatura: assinaturaData,
    })
  }

  // Opções dos selects de edição (react-select), preservando os ids no estado.
  const tipoOptions = tiposServico
    .filter(t => t.NAME && t.NAME.trim())
    .map(t => ({
      value: t.OID_TIPO,
      label: t.NAME,
    }))
  const statusOptions = [
    { value: "ABERTO", label: "Em aberto" },
    { value: "ANDAMENTO", label: "Em andamento" },
    { value: "CONCLUIDA", label: "Concluída" },
    { value: "CANCELADA", label: "Cancelada" },
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Informações da OS */}
          <Row className="mb-4">
            <Col lg={9}>
              <Card className="h-100 mb-0">
                <CardBody>
                  {/* Cabeçalho com ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                    <div className="d-flex align-items-center">
                      <Button
                        color="link"
                        className="p-0 me-3"
                        onClick={() => navigate("/ordem-servico")}
                      >
                        <i className="bx bx-arrow-back font-size-24"></i>
                      </Button>
                      <h4 className="card-title mb-0 me-3">Ordem de Serviço</h4>
                      <span className="text-primary fw-bold">
                        {os.PROTOCOLO ||
                          `OS-${String(os.OID_OS).padStart(4, "0")}`}
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      {!editMode ? (
                        <>
                          <Button color="info" onClick={baixarPdf}>
                            <i className="bx bxs-file-pdf me-1"></i>
                            Salvar PDF
                          </Button>
                          <Button
                            color="warning"
                            onClick={() => setEditMode(true)}
                            disabled={!editavel}
                            title={
                              editavel
                                ? undefined
                                : "OS concluída, cancelada ou com status inválido não pode ser editada"
                            }
                          >
                            <i className="bx bx-edit me-1"></i>
                            Editar
                          </Button>
                          <Button
                            color="danger"
                            onClick={excluirOS}
                            disabled={concluida}
                          >
                            <i className="bx bx-trash me-1"></i>
                            Excluir
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            color="secondary"
                            onClick={() => setEditMode(false)}
                            disabled={salvando}
                          >
                            Cancelar
                          </Button>
                          <Button
                            color="success"
                            onClick={salvarEdicao}
                            disabled={salvando}
                          >
                            {salvando ? (
                              <>
                                <Spinner size="sm" className="me-1" />
                                Salvando...
                              </>
                            ) : (
                              <>
                                <i className="bx bx-save me-1"></i>
                                Salvar
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <h5 className="mb-4">
                    <i className="bx bx-file me-2 text-primary"></i>
                    Informações da OS
                  </h5>

                  <Row className="g-3">
                    <Col xs={12} sm={6} md={4}>
                      <label className="text-muted small">Protocolo</label>
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editData.PROTOCOLO}
                          onChange={e =>
                            handleEditChange("PROTOCOLO", e.target.value)
                          }
                        />
                      ) : (
                        <p className="fw-bold mb-0">
                          {os.PROTOCOLO ||
                            `OS-${String(os.OID_OS).padStart(4, "0")}`}
                        </p>
                      )}
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <label className="text-muted small">Data</label>
                      <p className="mb-0 fw-semibold">
                        {formatarData(os.DT_ULT_ALTER || os.DT_CRIACAO)}
                      </p>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <label className="text-muted small">Status</label>
                      {editMode ? (
                        <Select
                          options={statusOptions}
                          value={
                            statusOptions.find(
                              o => o.value === editData.STATUS,
                            ) || null
                          }
                          onChange={opt =>
                            handleEditChange("STATUS", opt ? opt.value : "")
                          }
                          styles={selectMenuStyles}
                        />
                      ) : (
                        <div>
                          <StatusBadge
                            color={statusInfo.color}
                            label={statusInfo.label}
                          />
                        </div>
                      )}
                    </Col>

                    <Col xs={12} sm={6} md={4}>
                      <label className="text-muted small">
                        Condomínio/Empresa
                      </label>
                      <p className="mb-0 fw-semibold">{getNomeCondominio(os.OID_EMPRESA)}</p>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <label className="text-muted small">
                        Responsável Técnico
                      </label>
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editData.RESPONSAVEL}
                          onChange={e =>
                            handleEditChange("RESPONSAVEL", e.target.value)
                          }
                        />
                      ) : (
                        <p className="mb-0 fw-semibold">
                          <i className="bx bx-user-circle me-1"></i>
                          {os.RESPONSAVEL || "Não informado"}
                        </p>
                      )}
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <label className="text-muted small">
                        Tipo de Serviço
                      </label>
                      {editMode ? (
                        <Select
                          placeholder="Selecione..."
                          options={tipoOptions}
                          value={
                            tipoOptions.find(
                              o =>
                                String(o.value) === String(editData.TIPO_ORDEM),
                            ) || null
                          }
                          onChange={opt =>
                            handleEditChange("TIPO_ORDEM", opt ? opt.value : "")
                          }
                          isClearable
                          styles={selectMenuStyles}
                        />
                      ) : (
                        <p className="mb-0 fw-semibold">
                          <i className="bx bx-wrench me-1 text-primary"></i>
                          {getNomeTipo(os.TIPO_ORDEM)}
                        </p>
                      )}
                    </Col>

                    <Col xs={12} sm={6} md={4}>
                      <label className="text-muted small">
                        Item / Quantidade
                      </label>
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editData.DS_ITEM}
                          onChange={e =>
                            handleEditChange("DS_ITEM", e.target.value)
                          }
                        />
                      ) : (
                        <p className="mb-0 fw-semibold">{os.DS_ITEM || "Não informado"}</p>
                      )}
                    </Col>
                    <Col xs={12} md={8}>
                      <label className="text-muted small">Descrição</label>
                      {editMode ? (
                        <textarea
                          className="form-control"
                          rows={4}
                          value={editData.DS_ORDEM}
                          onChange={e =>
                            handleEditChange("DS_ORDEM", e.target.value)
                          }
                        />
                      ) : (
                        <p className="mb-0 fw-semibold">{os.DS_ORDEM || "Sem descrição"}</p>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Resumo */}
            <Col lg={3}>
              <Card className="h-100 mb-0">
                <CardBody className="d-flex flex-column">
                  <h5 className="mb-3">
                    <i className="bx bx-info-circle me-2 text-primary"></i>
                    Resumo
                  </h5>
                  <div className="d-flex flex-column gap-3">
                    <div className="border rounded p-2 px-3 bg-light bg-opacity-50">
                      <small className="text-muted d-block">ID da OS</small>
                      <h5 className="mb-0 fw-bold">{os.OID_OS}</h5>
                    </div>
                    <div className="border rounded p-2 px-3 bg-light bg-opacity-50">
                      <small className="text-muted d-block">Condomínio</small>
                      <h5 className="mb-0 fw-bold">
                        {getNomeCondominio(os.OID_EMPRESA)}
                      </h5>
                    </div>
                    <div className="border rounded p-2 px-3 bg-light bg-opacity-50">
                      <small className="text-muted d-block">
                        Última Atualização
                      </small>
                      <p className="mb-0 fw-semibold">
                        {formatarData(os.DT_ULT_ALTER)}
                      </p>
                    </div>
                    <div className="border rounded p-2 px-3 bg-light bg-opacity-50">
                      <small className="text-muted d-block">Total de Anexos</small>
                      <h5 className="mb-0 fw-bold text-primary">
                        {anexos.length}
                      </h5>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Anexos */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <h5 className="mb-0">
                      <i className="bx bx-paperclip me-2 text-primary"></i>
                      Anexos ({anexos.length})
                    </h5>
                    <Button color="primary" onClick={abrirModalNovoAnexo}>
                      <i className="bx bx-plus me-1"></i>
                      Novo Anexo
                    </Button>
                  </div>

                  {anexos.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="bx bx-file font-size-48 text-muted mb-3 d-block"></i>
                      <p className="text-muted">Nenhum anexo adicionado</p>
                      <Button
                        color="primary"
                        outline
                        onClick={abrirModalNovoAnexo}
                      >
                        Adicionar primeiro anexo
                      </Button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>ID</th>
                            <th>Descrição</th>
                            <th>Item</th>
                            <th>Qtd</th>
                            <th>Responsável</th>
                            <th>Recebimento</th>
                            <th className="text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {anexos.map(anexo => (
                            <tr key={anexo.OID_ANEXO}>
                              <td>{anexo.OID_ANEXO}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span>{anexo.DS_ANEXO || "-"}</span>
                                  {anexo.IMAGEM_BASE64_STRING && (
                                    <Button
                                      color="light"
                                      size="sm"
                                      title="Visualizar imagem"
                                      onClick={() => {
                                        Swal.fire({
                                          imageUrl: `data:image/png;base64,${anexo.IMAGEM_BASE64_STRING}`,
                                          imageAlt: anexo.DS_ANEXO || "Imagem",
                                          showCloseButton: true,
                                          showConfirmButton: false,
                                          width: "auto",
                                          customClass: {
                                            image: "img-fluid",
                                          },
                                        })
                                      }}
                                    >
                                      <i className="bx bx-image"></i>
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td>{anexo.DS_ITEM || "-"}</td>
                              <td>{anexo.QTD_ITEM || "-"}</td>
                              <td>{anexo.RESPONSAVEL || "-"}</td>
                              <td>
                                {anexo.NOME_RECEBIMENTO && (
                                  <div>
                                    <small>{anexo.NOME_RECEBIMENTO}</small>
                                    {anexo.EMAIL_RECEBIMENTO && (
                                      <div className="text-muted small">
                                        {anexo.EMAIL_RECEBIMENTO}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {!anexo.NOME_RECEBIMENTO && "-"}
                              </td>
                              <td className="text-center">
                                <div className="d-flex justify-content-center gap-2">
                                  <BotaoAcao
                                    iconClassName={`bx bx-mail-send align-middle ${
                                      anexo.EMAIL_RECEBIMENTO ? "" : "text-muted"
                                    }`}
                                    title="Enviar email"
                                    disabled={!anexo.EMAIL_RECEBIMENTO}
                                    style={
                                      !anexo.EMAIL_RECEBIMENTO
                                        ? {
                                            opacity: 0.4,
                                            cursor: "not-allowed",
                                          }
                                        : undefined
                                    }
                                    onClick={() => enviarEmailAnexo(anexo)}
                                  />
                                  <BotaoAcao
                                    iconClassName="bx bx-edit align-middle"
                                    title="Editar"
                                    onClick={() => abrirModalEditarAnexo(anexo)}
                                  />
                                  <BotaoAcao
                                    iconClassName="bx bx-trash align-middle text-danger"
                                    title="Excluir"
                                    onClick={() => excluirAnexo(anexo)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal de Anexo */}
      <ModalFormOS
        isOpen={modalAnexo}
        toggle={() => setModalAnexo(false)}
        size="lg"
        title={anexoSelecionado ? "Editar Anexo" : "Novo Anexo"}
        footer={
          <>
            <Button
              color="secondary"
              onClick={() => setModalAnexo(false)}
              disabled={salvandoAnexo}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              onClick={salvarAnexo}
              disabled={salvandoAnexo}
            >
              {salvandoAnexo ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Salvando...
                </>
              ) : (
                <>
                  <i className="bx bx-save me-1"></i>
                  Salvar
                </>
              )}
            </Button>
          </>
        }
      >
        <Row>
          <Col md={12}>
            <label className="form-label">
              Arquivo{" "}
              {!anexoSelecionado && <span className="text-danger">*</span>}
              {anexoSelecionado && (
                <span className="text-muted small fw-normal">
                  {" "}
                  (opcional — deixe em branco para manter o atual)
                </span>
              )}
            </label>
            <input
              type="file"
              className="form-control"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            {novoAnexo.file && (
              <small className="text-success">
                <i className="bx bx-check me-1"></i>
                {novoAnexo.file.name}
              </small>
            )}
            {anexoSelecionado && !novoAnexo.file && (
              <small className="text-muted d-block mt-1">
                Anexo atual:{" "}
                {anexoSelecionado.DS_ANEXO ||
                  anexoSelecionado.DS_ITEM ||
                  `#${anexoSelecionado.OID_ANEXO}`}
              </small>
            )}
          </Col>
        </Row>

        <Row className="mt-2">
          <Col md={12}>
            <label className="form-label">Descrição do Anexo</label>
            <input
              type="text"
              className="form-control"
              value={novoAnexo.DS_ANEXO}
              onChange={e => handleAnexoChange("DS_ANEXO", e.target.value)}
              placeholder="Ex: Assinatura do cliente, Foto do equipamento..."
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col md={6}>
            <label className="form-label">Item</label>
            <input
              type="text"
              className="form-control"
              value={novoAnexo.DS_ITEM}
              onChange={e => handleAnexoChange("DS_ITEM", e.target.value)}
              placeholder="Nome do produto/item"
            />
          </Col>
          <Col md={6}>
            <label className="form-label">Quantidade</label>
            <input
              type="number"
              className="form-control"
              value={novoAnexo.QTD_ITEM}
              onChange={e => handleAnexoChange("QTD_ITEM", e.target.value)}
              placeholder="0"
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col md={12}>
            <label className="form-label">Responsável</label>
            <input
              type="text"
              className="form-control"
              value={novoAnexo.RESPONSAVEL}
              onChange={e => handleAnexoChange("RESPONSAVEL", e.target.value)}
              placeholder="Nome do responsável"
            />
          </Col>
        </Row>

        <hr />
        <h6 className="text-muted mb-3">
          <i className="bx bx-envelope me-2"></i>
          Informações de Recebimento (para envio de email)
        </h6>

        <Row>
          <Col md={6}>
            <label className="form-label">Nome de Recebimento</label>
            <input
              type="text"
              className="form-control"
              value={novoAnexo.NOME_RECEBIMENTO}
              onChange={e =>
                handleAnexoChange("NOME_RECEBIMENTO", e.target.value)
              }
              placeholder="Nome do cliente"
            />
          </Col>
          <Col md={6}>
            <label className="form-label">Email de Recebimento</label>
            <input
              type="email"
              className="form-control"
              value={novoAnexo.EMAIL_RECEBIMENTO}
              onChange={e =>
                handleAnexoChange("EMAIL_RECEBIMENTO", e.target.value)
              }
              placeholder="cliente@email.com"
            />
          </Col>
        </Row>
      </ModalFormOS>
    </React.Fragment>
  )
}

export default DetalhesOS
